import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import DashboardLayout from 'analysis-tool/ui/components/DashboardLayout';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {
  ParameterOptimizationPredictionRequestData,
  ParameterOptimizationPredictionResponse,
} from 'machine-learning/MachineLearningDAO';
import PredictionOutput from 'machine-learning/parameter-optimization/prediction/PredictionOutput';
import {PredictionParameter} from 'machine-learning/parameter-optimization/prediction/PredictionParametersTypes';
import PredictionsScatterPlot from 'machine-learning/parameter-optimization/prediction/PredictionsScatterPlot';
import prettyChartNumber from 'ui-library/charting/util/prettyChartNumber';
import {AsyncState, AsyncStatus} from 'ui-library/hooks/useAsyncState';
import Pane, {PaneBorder} from 'ui-library/pane/Pane';
import {Column, Table} from 'ui-library/simple-table/SimpleTable';

const BLOCK_HEIGHT = 400;
const PREDICTION_STATISTIC_NAMES = ['Lower Bound', 'Upper Bound', 'Interval Width', 'Uncertainty'];

export interface PredictionDashboardProps {
  allData: DataFrame;
  parametersForOutput: Array<PredictionParameter>;
  output: AsyncState<ParameterOptimizationPredictionResponse, [ParameterOptimizationPredictionRequestData]>;
}

export default function PredictionDashboard({
  allData,
  parametersForOutput,
  output,
}: PredictionDashboardProps): JSX.Element {
  let outputStatisticsSection = null;
  if (output.status === AsyncStatus.SUCCESS) {
    const outputStatisticsList = [
      output.data.lowerBound,
      output.data.upperBound,
      output.data.intervalWidth,
      output.data.uncertainty,
    ];
    outputStatisticsSection = (
      <DashboardLayout
        blockSpecs={[
          {
            key: 'prediction stats',
            block: (
              <Pane fitContent={true} paneBorder={PaneBorder.SOLID} title="Prediction statistics">
                <Table
                  className={css(styles.table)}
                  isCompact={true}
                  numRows={PREDICTION_STATISTIC_NAMES.length}
                  showBorder={false}
                >
                  <Column
                    header="Statistic"
                    renderCell={(rowIndex) => PREDICTION_STATISTIC_NAMES[rowIndex]}
                  />
                  <Column
                    header="Value"
                    renderCell={(rowIndex) => prettyChartNumber(outputStatisticsList[rowIndex])}
                  />
                </Table>
              </Pane>
            ),
            minHeight: 0,
            maxHeight: BLOCK_HEIGHT,
          },
          {
            key: 'nearest point',
            block: (
              <Pane fitContent={true} paneBorder={PaneBorder.SOLID} title="Parameter value comparison">
                <Table
                  className={css(styles.table)}
                  isCompact={true}
                  numRows={parametersForOutput.length}
                  showBorder={false}
                >
                  <Column
                    header="Parameter name"
                    renderCell={(rowIndex) => parametersForOutput[rowIndex].displayName}
                  />
                  <Column
                    header="Entered value"
                    renderCell={(rowIndex) =>
                      parametersForOutput[rowIndex].valueInput.isDropdown
                        ? parametersForOutput[rowIndex].valueInput.value
                        : prettyChartNumber(parametersForOutput[rowIndex].valueInput.value as number)
                    }
                  />
                  <Column
                    header="Nearest match"
                    renderCell={(rowIndex) =>
                      parametersForOutput[rowIndex].valueInput.isDropdown
                        ? output.data.point[parametersForOutput[rowIndex].systemName]
                        : prettyChartNumber(
                            output.data.point[parametersForOutput[rowIndex].systemName] as number
                          )
                    }
                  />
                </Table>
              </Pane>
            ),
            minHeight: 0,
            maxHeight: BLOCK_HEIGHT,
          },
        ]}
      />
    );
  }

  const chartsSection = (
    <DashboardLayout
      blockSpecs={[
        {
          key: 'Predictions scatter plot',
          block: (
            <PredictionsScatterPlot
              allData={allData}
              predictionOutput={output.status === AsyncStatus.SUCCESS ? output.data : null}
            />
          ),
          minHeight: BLOCK_HEIGHT,
          maxHeight: BLOCK_HEIGHT,
        },
      ]}
    />
  );

  return (
    <>
      <PredictionOutput output={output} />
      {chartsSection}
      {outputStatisticsSection}
    </>
  );
}

const styles = StyleSheet.create({
  table: {
    borderRadius: 0,
    marginBottom: 0,
  },
});
